import { useEffect, useRef } from 'react'
import { axiosPrivate } from '../axios'
import useRefreshToken from './useRefreshToken'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from '../../context/auth/AuthContextProvider'
import useToastNotification from '../../hooks/useToastNotification'

const useAxiosPrivate = () => {
  const { user, userSignOut } = useAuthContext()
  const refresh = useRefreshToken()
  const navigate = useNavigate()
  const { showApiErrorMessage } = useToastNotification()

  // Interceptor closures read these refs instead of the values directly, so the
  // registration effect below can run once per mount instead of on every render.
  const tokenRef = useRef(user.token)
  const refreshRef = useRef(refresh)
  const userSignOutRef = useRef(userSignOut)
  const navigateRef = useRef(navigate)
  const showApiErrorMessageRef = useRef(showApiErrorMessage)

  tokenRef.current = user.token
  refreshRef.current = refresh
  userSignOutRef.current = userSignOut
  navigateRef.current = navigate
  showApiErrorMessageRef.current = showApiErrorMessage

  useEffect(() => {
    // Add a request interceptor
    const requestIntercept = axiosPrivate.interceptors.request.use(
      (config) => {
        if (!config.headers['Authorization'] || !tokenRef.current) {
          config.headers.Authorization = `Bearer ${tokenRef.current}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Add a response interceptor
    const responseIntercept = axiosPrivate.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        if (
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry
        ) {
          // If the error status is 401 and there is no originalRequest._retry flag,
          // it means the token has expired and we need to refresh it
          originalRequest._retry = true

          // Sent before another request refreshed the token, so retry with the new one
          const currentAuthorization = `Bearer ${tokenRef.current}`
          if (
            tokenRef.current &&
            originalRequest.headers.Authorization !== currentAuthorization
          ) {
            originalRequest.headers.Authorization = currentAuthorization

            return axiosPrivate(originalRequest)
          }

          const newJwtToken = await refreshRef.current()
          if (newJwtToken === null) {
            // BE explains why (account disabled, password expired...), show it before signing out
            showApiErrorMessageRef.current(
              error,
              'Your session has expired. Please sign in again.'
            )
            userSignOutRef.current()
            navigateRef.current('/')

            return
          }

          originalRequest.headers.Authorization = `Bearer ${newJwtToken}`

          return axiosPrivate(originalRequest)
        }

        return Promise.reject(error)
      }
    )

    return () => {
      axiosPrivate.interceptors.request.eject(requestIntercept)
      axiosPrivate.interceptors.response.eject(responseIntercept)
    }
  }, [])

  return axiosPrivate
}

export default useAxiosPrivate
